// The tube reload recipe kit: the input every tube reload look draws from,
// and the arithmetic it draws with (R1474, `ui-map-interface.md` §6.3).
//
// A TUBE RELOAD look is what an own hull with tubes draws for its tube
// reload, the rounds in its magazine and the kind of round it carries. The
// World theme field `reload` names one of eight, the cards the owner picked
// from `archive/mockups/064-tube-reload.html`. `reload-styles.ts` is the
// registry that answers a look id, and `reload-recipes.ts` holds the draw
// code. This module is what both of them import.
//
// This is a leaf (`leaves.test.ts`). It names the sinks of a layer through
// a type-only clause, the way `holdfire-recipe-kit.ts` does. The fleet
// layer owns the wire, the clock, the cull and the batches, and it hands
// them in as ONE reused context a hull.
//
// **Nothing here allocates on the frame path.** A recipe runs once per own
// hull with tubes, every frame (§10.1). A word a look writes comes from a
// cache of `reload-recipes.ts`, so a frame builds no string.
//
// **The look is own-only.** `reload_sec`, `torpedoes` and `reload_full_sec`
// ride to the owner alone, so the context carries no faction and no fainter
// alpha. Every look wears the inks of `[reload.limits]`.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP CARD MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards of round 064 draw in WORLD px with the hull at the origin, on a
// canvas that is NOT turned (card 07 turns its own frame by the heading).
// The translation is fixed:
//
//   | the card writes                  | the recipe writes                  |
//   | -------------------------------- | ---------------------------------- |
//   | `x`, `y`, `o.head`               | `c.x`, `c.y`, `c.head`             |
//   | `st.z`                           | `c.z`                              |
//   | `s.empty`, `s.reloading`         | `c.empty`, `c.reloading`           |
//   | `s.ready`, `s.p`                 | `c.ready`, `c.p`                   |
//   | `Math.ceil(s.rem)`               | `c.leftSec`, whole WORLD seconds   |
//   | `s.count`, `s.mag`, `s.kind`     | `c.count`, `c.mag`, `c.shape`      |
//   | `KIND[s.kind]`, `s.kind` letter  | `c.name`, `c.letter`               |
//   | `s.firedAgo`, `s.readyAgo`       | `c.firedAgo`, `c.readyAgo`         |
//   | `INK`, `WHITE`, `FAINT`, `DEEP`  | `RELOAD_LIMITS.ink`, `.flash`, ... |
//   | `lw(w)`                          | `lw(c, w)`                         |
//   | `flash(ago, d)`                  | `flash(ago, d)`                    |
//   | `arcS(c, …)`, no dash            | `arc(c, …)`: round caps            |
//   | `circle(c, …)`                   | `ring(c, …)`                       |
//   | `line(c, …)`                     | `line(c, …)`                       |
//   | `c.fillRect(…)`                  | `rect(c, …)`                       |
//   | `text(c, …)`                     | `c.text(…)`                        |
//
// Every FIGURE came out of the card and stands in the look's `fig` table of
// `data/presentation/reload.toml`.

import { RELOAD_LIMITS } from '../gen/presentation';
import type { ReloadRow } from '../gen/presentation';
import type { CircleSink, SegmentSink } from '../fast/layers/batches';
import { TAU, fillFor, strokeFor } from './recipe-kit';
import { TorpedoKind } from '../gen/proto/aquaphobia';

export type { ReloadRow };
export { TAU, clamp01, frac } from './recipe-kit';

/** How the kind of the round reads as a shape: the card's square for the
 *  Harpoon, diamond for the Remora and disc for the Torpor. */
export type ReloadShape = 'square' | 'diamond' | 'disc';

/** Where a run of text stands against its point. */
export type ReloadAnchor = 'left' | 'centre';

/**
 * Where a look's words go. The fleet layer answers it with the chart's mono
 * glyph cells (§17), so the map bakes no second face. The run stands at
 * `(x, y)` in WORLD px and grows with the map the way the card's text does.
 * `halo` asks for the cells with the dark halo under the ink. It answers
 * the width of the run in world px.
 */
export type ReloadText = (
  s: string, x: number, y: number, sizePx: number, ink: number, alpha: number,
  anchor: ReloadAnchor, halo: boolean,
) => number;

/**
 * ONE OWN HULL WITH TUBES, as a recipe reads it. The layer reuses ONE of
 * these (R131).
 *
 * Every length is world px and every angle is radians, with +x east and +y
 * south. An angle turns clockwise on the screen, the canvas rule.
 */
export interface ReloadCtx {
  /** The camera scale: CSS px for one world px. */
  z: number;
  /** The figure row of the look that draws. */
  row: ReloadRow;
  /** The engine's round-cap flag (`fast/layer.ts` `F_ROUND`), handed in so
   *  this leaf takes no engine import. */
  round: number;
  /** The hull's centre, in world px, and its heading. */
  x: number;
  y: number;
  head: number;
  /** No round aboard: the tubes have nothing to load, so they do not
   *  reload. */
  empty: boolean;
  /** A round aboard and the tube not ready yet. */
  reloading: boolean;
  /** A round aboard and the tube ready. */
  ready: boolean;
  /** The share of the reload done, 0 to 1; 1 when the tube is ready. */
  p: number;
  /** The whole WORLD seconds left, the figure the fleet card prints. */
  leftSec: number;
  /** The rounds aboard, and the magazine they fill. */
  count: number;
  mag: number;
  /** The kind as a shape, the kind's initial and its name in capitals. */
  shape: ReloadShape;
  /** The shape of each round aboard, in the order the hull fires them
   *  (R2031): a mixed load draws each round in its own shape. A place past
   *  its end takes `shape`. */
  shapes: ReloadShape[];
  letter: string;
  name: string;
  /** WALL seconds since the shot and since the tube came ready; a large
   *  figure when the client never saw it happen. */
  firedAgo: number;
  readyAgo: number;
  /** The circles under the strokes, the strokes and the circles over them.
   *  A look that draws under the hull gets ONE circle sink in both. */
  ground: CircleSink;
  segs: SegmentSink;
  rings: CircleSink;
  /** The text sink. */
  text: ReloadText;
  /** The width of a run the text sink would write, in world px. */
  measure: (s: string, sizePx: number) => number;
}

/** ONE TUBE RELOAD LOOK: what it draws for one hull in one frame. */
export type ReloadRecipe = (c: ReloadCtx) => void;

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/** A flash that starts at 1 and falls to 0 over `d` seconds after `ago`.
 *  It is `flash` of the page. */
export function flash(ago: number, d: number): number {
  return ago >= 0 && ago < d ? 1 - ago / d : 0;
}

/** Slow at the start and slow at the end: the card's quadratic `ease`,
 *  which is not the smoothstep of `recipe-kit.ts`. */
export function easeQuad(p: number): number {
  return p < 0.5 ? 2 * p * p : 1 - ((-2 * p + 2) * (-2 * p + 2)) / 2;
}

/**
 * The drawn width of a stroke the card gave as `w`: never under
 * `min_screen_px` on the screen. It is `lw` of the page.
 */
export function lw(c: ReloadCtx, w: number): number {
  return Math.max(w, RELOAD_LIMITS.minScreenPx / c.z);
}

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// Each one clamps its alpha under its ceiling of `[reload.limits]` (a fill
// under the fill ceiling, a stroke under the stroke ceiling), puts
// every stroke width through `lw` and skips a mark that would draw nothing,
// so a recipe states the card's figure and never guards it. A fill takes no
// floor: the card fills a shape at its own size.

/** A stroke alpha, held under the stroke ceiling. */
export const strokeAlpha = strokeFor(RELOAD_LIMITS);

/** A fill alpha, held under the fill ceiling. */
const fillAlpha = fillFor(RELOAD_LIMITS);

/** A stroked full circle. It is `circle` of the page with no dash. */
export function ring(
  c: ReloadCtx, sink: CircleSink, x: number, y: number, r: number, w: number,
  ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || al <= 0) return;
  sink.ring(x, y, r, lw(c, w), ink, al);
}

/** A filled disc. */
export function disc(
  sink: CircleSink, x: number, y: number, r: number, ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (r <= 0 || al <= 0) return;
  sink.disc(x, y, r, ink, al);
}

/** A filled sector from `start` over `sweep`: a circle of no stroke cut
 *  to its arc. It is the card's `moveTo(cx, cy); arc(…); fill()`. */
export function sector(
  sink: CircleSink, x: number, y: number, r: number, start: number, sweep: number,
  ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (r <= 0 || sweep <= 0 || al <= 0) return;
  sink.arc(x, y, r, 0, start, sweep, ink, al);
}

/**
 * A stroked arc from `start` over `sweep`, with the round caps of the
 * card's `lineCap = 'round'`. The circle program cuts an arc square at its
 * two ends, so each cap is a half disc of half the width, turned out along
 * the arc: no cap overlaps the arc, so a faint arc takes no brighter end.
 * `caps = false` keeps the square ends, for an arc that meets a stroke.
 */
export function arc(
  c: ReloadCtx, sink: CircleSink, x: number, y: number, r: number, w: number,
  start: number, sweep: number, ink: number, a: number, caps = true,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || sweep <= 0 || al <= 0) return;
  const d = lw(c, w);
  sink.arc(x, y, r, d, start, sweep, ink, al);
  if (!caps) return;
  const end = start + sweep;
  sink.arc(x + Math.cos(start) * r, y + Math.sin(start) * r, d / 2, 0, start - Math.PI, Math.PI, ink, al);
  sink.arc(x + Math.cos(end) * r, y + Math.sin(end) * r, d / 2, 0, end, Math.PI, ink, al);
}

/**
 * A dashed arc from `start` over `sweep`, `on` px of dash and `off` px of
 * gap along the rim, with the butt caps of the card's dashed `arcS`. A
 * `sweep` of a whole turn is a dashed ring.
 */
export function dashedArc(
  c: ReloadCtx, sink: CircleSink, x: number, y: number, r: number, w: number,
  start: number, sweep: number, on: number, off: number, ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || sweep <= 0 || on <= 0 || al <= 0) return;
  const count = (TAU * r) / (on + off);
  sink.dashed(x, y, r, lw(c, w), count, on / (on + off), ink, al, start, sweep);
}

/** A straight stroke in world px, with round caps unless `round` is false.
 *  It is `line` of the page. */
export function line(
  c: ReloadCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, ink: number, a: number, round = true,
): void {
  const al = strokeAlpha(a);
  if (w <= 0 || al <= 0) return;
  c.segs.line(x0, y0, x1, y1, lw(c, w), ink, al, round ? c.round : 0);
}

/** A dashed straight stroke, `on` px of dash and `off` px of gap. */
export function dashedLine(
  c: ReloadCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, on: number, off: number, ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (w <= 0 || al <= 0) return;
  c.segs.line(x0, y0, x1, y1, lw(c, w), ink, al, 0, on, off);
}

/**
 * A filled axis-aligned rectangle, from its top left corner. It is one
 * stroke with butt caps: the short side is the width of the stroke, the
 * way the fleet layer draws its bars.
 */
export function rect(
  c: ReloadCtx, x: number, y: number, w: number, h: number, ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (w <= 0 || h <= 0 || al <= 0) return;
  if (w >= h) c.segs.line(x, y + h / 2, x + w, y + h / 2, h, ink, al, 0);
  else c.segs.line(x + w / 2, y, x + w / 2, y + h, w, ink, al, 0);
}

/**
 * A stroked axis-aligned rectangle. It is `strokeRect` of the page: four
 * strokes centred on the four edges, the two long ones run out by half the
 * width, so the corners close square and no corner is drawn twice.
 */
export function rectStroke(
  c: ReloadCtx, x: number, y: number, w: number, h: number,
  width: number, ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (w <= 0 || h <= 0 || width <= 0 || al <= 0) return;
  const d = lw(c, width), k = d / 2;
  c.segs.line(x - k, y, x + w + k, y, d, ink, al, 0);
  c.segs.line(x - k, y + h, x + w + k, y + h, d, ink, al, 0);
  c.segs.line(x, y + k, x, y + h - k, d, ink, al, 0);
  c.segs.line(x + w, y + k, x + w, y + h - k, d, ink, al, 0);
}

/**
 * The shape of one round kind, the card's `kindPath`: the Remora is a
 * diamond, the Torpor a disc, and the Harpoon, like any other kind, a
 * square. Card 07 reads the same answer: a ring at the nose for the
 * diamond, a band for the disc.
 *
 * The five rounds of the torpedo refresh (R2779, R2783) take the shape of
 * what they do, until a mockup round gives them shapes of their own: a
 * round that chases a lock is a diamond (the Wrasse), a round that lays an
 * area effect and strikes no hull is a disc (the Sepia, the Sailfish), and
 * a round that strikes is a square (the Countermine, the Stonefish).
 */
export function shapeOf(kind: number): ReloadShape {
  switch (kind) {
    case TorpedoKind.TORPEDO_KIND_SEEKER:
    case TorpedoKind.TORPEDO_KIND_WRASSE:
      return 'diamond';
    case TorpedoKind.TORPEDO_KIND_DISRUPTOR:
    case TorpedoKind.TORPEDO_KIND_SEPIA:
    case TorpedoKind.TORPEDO_KIND_SAILFISH:
      return 'disc';
    default: return 'square';
  }
}
